import { from } from 'rxjs';
import { filter } from 'rxjs/operators';
import ApiClient from '@shoflo/api.v2-client';
import { CAMERA_PRESENTER } from '@shoflo/polymer/constants/SourceItems';
import { SOURCE_ROLE_SPEAKER } from '@shoflo/polymer/constants/SourceRoleTypes';
import { BROADCAST_ACCEPT_KNOCK, BROADCAST_UPDATE_KNOCK } from 'actions/Types';
import { getBroadcastRouteMatch } from 'actions/utils';
import { CHAT_FULL_ACCESS } from 'constants/Permissions';
import { PRESENTER_BANNER } from 'constants/Banners';
import { KNOCK_ANSWER, KNOCK_FAIL } from 'store/eventConstants';
import { UNASSIGNED } from 'utils/broadcastSourceHelper';
import { withTimeout, recursiveFunction } from 'utils/utils';
import { selectLastPresenterSource } from 'selectors/broadcastSourcesSelector';
import { logProgramChange } from '@shoflo/studio-redux/utils/sendToDatadogWithIds';
import {
  getStreamingStatus,
  getRecordingStatus
} from '@shoflo/studio-redux/utils/streamHelper';
import setAttendeeName from '@shoflo/studio-redux/actions/attendee/setAttendeeName';
import clearKnock from '@shoflo/studio-redux/actions/knock/clearKnock';
import showModal from '@shoflo/studio-redux/actions/modal/showModal/showModal';
import showNotification from '@shoflo/studio-redux/actions/notification/showNotification';
import setUnassignedAttendeePresence from 'actions/Broadcast/attendees/setUnassignedAttendeePresence';
import setProgram from 'actions/Broadcast/program/setProgram';
import createSource from 'actions/Broadcast/sources/createSource';
import setIsInitializing from 'actions/Broadcast/sources/setIsInitializing';
import updateSource from 'actions/Broadcast/sources/updateSource';
import { shouldHaveFeature } from '@studio/feature.flags/featureFlags';
import { VENDOR_AGORA } from '@studio/constants/Vendor';
import { UNAUTHORIZED_ERROR } from '@studio/constants/ModalTypes';

import { ACCEPTED, PRESENTER, MAX_RETRIES } from 'constants/KnockTypes';

const DEFAULT_ORDER_NUMBER = 0;

/**
 * Emits an event to the accepted Presenter socket
 *
 * @param socket
 */
export const acceptedKnock = socket => store => next => async action => {
  const { dispatch, getState } = store;

  const targetdActions = [
    `${BROADCAST_ACCEPT_KNOCK}`,
    `${BROADCAST_UPDATE_KNOCK}_SUCCESS`
  ];

  from([action])
    .pipe(filter($action => targetdActions.includes($action.type)))
    .subscribe(async $action => {
      // Ensure knock is of type presenter and accepted
      if (
        $action.payload.type === PRESENTER &&
        $action.payload.status === ACCEPTED
      ) {
        const apiClient = getApiClient();
        let {
          meeting,
          attendeeRoster,
          program,
          team,
          broadcastSources,
          attendeeCameraStatus,
          broadcastSettings,
          banners,
          broadcastRecordings: { liveRecordings },
          broadcastStreamEndpoints: streamEndpoints
        } = getState();

        const match = getBroadcastRouteMatch(getState);

        const hasChatAccess = team
          ? team.permissions.some(({ name }) => name === CHAT_FULL_ACCESS)
          : false;

        const { teamId, eventId } = match.params;

        const useAgora =
          shouldHaveFeature('studio.meetingVendor') === VENDOR_AGORA;

        // Agora does not need the attendee created for them, so we just emit the
        // socket event with the knock info
        if (useAgora) {
          const presenterBanner = banners.find(
            banner => banner.source.value.templateType === PRESENTER_BANNER
          );

          const isRecording = getRecordingStatus(liveRecordings);
          const isStreaming = getStreamingStatus(streamEndpoints);

          const streamRecordStatus = {
            isRecording,
            isStreaming
          };

          const knockPayload = {
            broadcastId: $action.payload.broadcastId,
            socketId: $action.payload.socketId,
            knockId: $action.payload?._id,
            attendeeRoster,
            program,
            hasChatAccess,
            muteMeetingAudio: broadcastSettings.muteMeetingAudio || false,
            presenterBanner: presenterBanner || null,
            broadcastSettings,
            streamRecordStatus,
            sourceId: $action.payload.sourceId
          };

          socket.emit(
            KNOCK_ANSWER,
            knockPayload,
            withTimeout(
              () => {},
              () => {
                socket.emit(KNOCK_ANSWER, knockPayload);
              },
              3000
            )
          );
          dispatch(clearKnock($action.payload));
          return;
        }

        // Check to see if the meeting is in session and that there are acceptedKnocks
        if (!meeting.meetingSession) return;

        // Clear the knock from state
        dispatch(clearKnock($action.payload));

        let createdAttendee;

        async function createAttendeeRequest() {
          return apiClient.broadcast.createMeetingAttendee(
            teamId,
            eventId,
            $action.payload.broadcastId,
            meeting.meetingResponse.Meeting.MeetingId,
            {
              sourceId: $action.payload.sourceId,
              userType: 'Presenter',
              presenterId: $action.payload.presenterId,
              sessionId: $action.payload.sessionId,
              presenterName: $action.payload.name
            }
          );
        }

        // Create the attendee
        try {
          createdAttendee = await recursiveFunction(
            createAttendeeRequest,
            MAX_RETRIES
          );
        } catch (e) {
          console.log(e);
          socket.emit(KNOCK_FAIL, {
            ...$action.payload,
            knockId: $action.payload._id
          });
          if (e?.response?.status === 401) {
            dispatch(showModal(UNAUTHORIZED_ERROR));
            return;
          }
          const message = `${
            $action.payload?.name || 'Presenter'
          } failed to join!`;
          dispatch(
            showNotification(message, {
              timeout: 5500,
              type: 'warning'
            })
          );
        }

        if (!createdAttendee) {
          return;
        }
        const { attendeeResponse, chatToken, presenterToken } = createdAttendee;

        // The 'presenterSessionId' is utilized when a presenter joins a Chime meeting SLI.
        // It follows the format 'presenterSessionId: Presenter-{presenterId}-{sessionId}'.
        const presenterSessionId = attendeeResponse.Attendee.ExternalUserId;

        const { meetingResponse, mixerAttendeeIds } = meeting;

        if (
          $action.payload.sourceId &&
          isUnassignedSource($action.payload.sourceId, broadcastSources)
        ) {
          dispatch(
            setUnassignedAttendeePresence(attendeeResponse.Attendee.AttendeeId)
          );

          const isAuthenticatedUser = !!$action.payload?.userId;
          if (isAuthenticatedUser) {
            dispatch(
              updateSource($action.payload.sourceId, {
                title: $action.payload.name,
                description: $action.payload.description,
                userId: $action.payload.userId,
                role: SOURCE_ROLE_SPEAKER,
                cameraUnavailable: $action.payload.cameraUnavailable
              })
            );
          }

          const updatedProgram = {
            ...program,
            layers: program?.layers
              ? program?.layers?.map(layer => {
                  if (layer?.source?._id === $action.payload?.sourceId) {
                    return {
                      ...layer,
                      source: {
                        ...layer.source,
                        value: attendeeResponse.Attendee.AttendeeId
                      }
                    };
                  }
                  return layer;
                })
              : []
          };
          dispatch(setProgram(updatedProgram));
          logProgramChange(
            getState,
            `acceptedKnock middleware - source id: ${$action.payload.sourceId}`
          );
          dispatch(
            updateSource($action.payload.sourceId, {
              title: $action.payload.name,
              description: $action.payload.description,
              cameraUnavailable: $action.payload.cameraUnavailable
            })
          );
        }
        // When there is no assigned source
        if (!$action.payload.sourceId) {
          let { order: lastPresenterOrder = DEFAULT_ORDER_NUMBER } =
            selectLastPresenterSource(getState());
          // Increment last presenter source order by 1
          const order = lastPresenterOrder + 1;
          dispatch(
            createSource(
              {
                order,
                title: $action.payload.name,
                type: CAMERA_PRESENTER,
                value: attendeeResponse.Attendee.AttendeeId,
                description: $action.payload?.description,
                userId: $action?.payload?.userId,
                role: SOURCE_ROLE_SPEAKER,
                presenterSessionId,
                cameraUnavailable: $action.payload.cameraUnavailable
              },
              true
            )
          );
        }

        dispatch(
          setAttendeeName(
            attendeeResponse.Attendee.AttendeeId,
            $action.payload.name,
            '',
            SOURCE_ROLE_SPEAKER
          )
        );

        const { AttendeeId: attendeeId } = attendeeResponse.Attendee;
        let { isCameraOn, isMicrophoneOn } = $action.payload || {};
        // Presenter should join with camera ON and MIC OFF by default
        if ('boolean' !== typeof isCameraOn) isCameraOn = true;
        if ('boolean' !== typeof isMicrophoneOn) isMicrophoneOn = false;
        // Allow director to override camera status
        const updatedCameraStatus = {
          ...attendeeCameraStatus,
          [attendeeId]: isCameraOn
        };

        // Get presenter banner
        const presenterBanner = banners.find(
          banner => banner.source.value.templateType === PRESENTER_BANNER
        );

        // Streaming/Recording status
        const isRecording = getRecordingStatus(liveRecordings);
        const isStreaming = getStreamingStatus(streamEndpoints);

        const streamRecordStatus = {
          isRecording,
          isStreaming
        };

        const knockPayload = {
          broadcastId: $action.payload.broadcastId,
          meetingResponse,
          attendeeResponse,
          mixerAttendeeIds,
          socketId: $action.payload.socketId,
          knockId: $action.payload?._id,
          attendeeRoster,
          program,
          chatToken,
          hasChatAccess,
          attendeeCameraStatus: updatedCameraStatus,
          knockAVStatus: {
            canControlMic: isMicrophoneOn
          },
          muteMeetingAudio: broadcastSettings.muteMeetingAudio || false,
          presenterBanner: presenterBanner || null,
          broadcastSettings,
          streamRecordStatus,
          presenterToken
        };

        // Send the meeting and attendee info over the socket
        socket.emit(
          KNOCK_ANSWER,
          knockPayload,
          withTimeout(
            () => {},
            () => {
              socket.emit(KNOCK_ANSWER, knockPayload);
            },
            3000
          )
        );

        if ($action.payload.sourceId) {
          // handle update of source display text here
          dispatch(
            setIsInitializing($action.payload.sourceId, {
              isInitializing: true,
              isAttendee: false,
              presenterSessionId
            })
          );
        }
      }
    });
  return next(action);
};

export default acceptedKnock;

/** Check if the source is unassigned */
const isUnassignedSource = (sourceId, broadcastSources) => {
  const source = broadcastSources?.sources.find(s => s._id === sourceId);
  return source?.value === UNASSIGNED;
};

function getApiClient() {
  const { hostname, protocol, port } = (window || {}).location;

  return new ApiClient({
    endpoint: `${protocol}//${hostname}:${port}`,
    proxy: true
  });
}
